import { useQuery } from '@apollo/client'
import { Alert } from '@designsforhealth/dfh-react-components'
import { Button, HorizontalProductCard, Spinner } from '@dfh-corp-sites/ui'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { ProtocolResourceQuery } from '../../../graphql/api'
import { SearchExactParentOrChildSkuDocument } from '../../../graphql/search'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import useRetailPrices from '../../../hooks/useRetailPrices'
import { ApolloClientsContext } from '../../../lib/graphql/apollo'
import useSanityImage from '../../../lib/sanity/hooks/useSanityImage'
import { formatAsCurrency } from '../../../utils/priceTools'
import Link from '../../global/Link'
import { useProtocolSimpleAddToCart } from './ProtocolSimpleAddToCart'

import Checkmark from '../../../img/icons/checkmark.svg'

export interface ProductProps {
  className?: string
  product: NonNullable<NonNullable<ProtocolResourceQuery['protocolResource']>['products']>[number]
}

export const Product: React.FC<ProductProps> = ({ product: item }) => {
  const [showSuccess, setShowSuccess] = useState(false)
  const quantity = item?.quantity ?? 0
  const sku = item?.product?.sku ?? ''
  const { shouldDisplayRetailPrice } = useRetailPrices()

  // Add to Cart hook
  const {
    onAdd,
    loading: addToCartLoading,
    errorMessage,
    success,
  } = useProtocolSimpleAddToCart(sku, quantity)

  useEffect(() => {
    if (!success) return
    setShowSuccess(true)
    const timer = setTimeout(() => setShowSuccess(false), 10000)
    return () => clearTimeout(timer)
  }, [success])

  const { comma: commaClient } = useContext(ApolloClientsContext)

  const { loading, data, error } = useQuery(SearchExactParentOrChildSkuDocument, {
    client: commaClient,
    context: {
      useLatestTokenIfLoggedIn: true,
    },
    variables: {
      sku: sku,
    },
    skip: !sku,
  })

  useLogAndCaptureError(error)

  // 1. Extract the fetched product
  const fetchedProduct = data?.exact?.results?.[0] || null

  const sanityImageUrl = useSanityImage(
    { sourceJson: fetchedProduct?.sanityImageJson || undefined },
    {
      dpr: 2,
      fit: 'max',
      width: 100,
    },
  )

  // 2. Format prices
  const productPrice = useMemo(() => {
    if (fetchedProduct?.priceRange?.minimum) {
      return formatAsCurrency({ amount: parseFloat(fetchedProduct.priceRange.minimum) })
    }
    return null
  }, [fetchedProduct])

  const productRetailPrice = useMemo(() => {
    if (fetchedProduct?.retailPriceRange?.maximum && shouldDisplayRetailPrice) {
      return formatAsCurrency({ amount: parseFloat(fetchedProduct.retailPriceRange.maximum) })
    }
    return null
  }, [fetchedProduct, shouldDisplayRetailPrice])

  // 3. Prepare product attributes
  const productAttributes = useMemo(() => {
    const urlKey = fetchedProduct?.urlKey || ''
    let attributeValues = ''
    let used_attributes: string[] = []

    if (fetchedProduct?.variants?.summary) {
      const values = fetchedProduct.variants.summary.values
      const attributes = fetchedProduct.variants.summary.attributes
      if (attributes && values) {
        const valueObj: Record<string, string> = {}
        used_attributes = attributes
        attributes.forEach((attr) => {
          const val = values[attr]
          if (Array.isArray(val) && val.length > 0) {
            valueObj[attr] = val[0]
          }
        })
        attributeValues = JSON.stringify(valueObj)
      }
    }
    return {
      urlKey,
      attributeValues,
      used_attributes,
    }
  }, [fetchedProduct])

  if (!sku || !quantity || !fetchedProduct) return null

  return !loading ? (
    <>
      <div key={fetchedProduct.name} className="flex flex-col lg:flex-row gap-2.5 lg:gap-5 w-full">
        <HorizontalProductCard
          title={<Link to={`/products/${fetchedProduct.urlKey}`}>{fetchedProduct.name || ''}</Link>}
          description={fetchedProduct.shortDescription?.text || ''}
          image={{ src: sanityImageUrl || '', alt: fetchedProduct.name }}
          quantity={quantity}
          price={productPrice || ''}
          retailPrice={productRetailPrice || ''}
          productAttributes={productAttributes}
          className="border-brand-grey rounded-lg max-w-[690px]"
        />
        {showSuccess ? (
          <div className="flex items-center gap-2 lg:w-[145px] justify-center">
            <div className="rounded-full bg-brand-categoryGreen w-6 h-6 flex items-center justify-center">
              <img src={Checkmark} alt="Check" />
            </div>
            Added
          </div>
        ) : (
          <div className="flex-1 flex items-start lg:items-center">
            <Button
              variant="text"
              className="text-brand-orange text-base sm:text-lg ml-5 lg:ml-0 lg:w-[145px]"
              onClick={onAdd}
              disabled={addToCartLoading || !onAdd}
            >
              {addToCartLoading ? <Spinner /> : 'Add to Cart'}
            </Button>
          </div>
        )}
      </div>
      {errorMessage && <Alert type="error">{errorMessage}</Alert>}
    </>
  ) : (
    <div>
      <Spinner />
    </div>
  )
}
